import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const IMG = "https://images.unsplash.com/photo-1642670270173-86ab9a78388a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwcnVyYWwlMjBmYXJtaW5nJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4Nzg4MjgxN3ww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/registre");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const input = "w-full bg-transparent border-b border-border py-3 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-secondary transition-colors";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(120_38%_10%)] to-[hsl(120_38%_10%)]/30" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-heading text-5xl text-[hsl(45_45%_97%)] leading-[0.95]">
            Le registre de<br /><span className="italic text-[hsl(16_57%_60%)]">notre patrimoine</span>
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-secondary mb-10">
            <ArrowLeft className="w-4 h-4" /> Retour au site
          </Link>
          <div className="flex items-center gap-2.5 mb-8">
            <img src="/logo.png" alt="ONG HERITAGES" className="w-12 h-12 rounded-full object-cover ring-1 ring-border" />
            <span className="font-heading text-2xl font-semibold text-primary">ONG HERITAGES</span>
          </div>
          <h1 className="font-heading text-4xl text-primary">Espace membre</h1>
          <p className="mt-2 text-muted-foreground">Accédez au registre foncier de l'organisation.</p>

          <form onSubmit={submit} data-testid="login-form" className="mt-10 space-y-6">
            <input type="email" required placeholder="Email" value={email}
              onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className={input} />
            <input type="password" required placeholder="Mot de passe" value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className={input} />
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full bg-primary text-primary-foreground font-medium hover:-translate-y-1 transition-transform disabled:opacity-60">
              {loading && <Loader2 className="w-5 h-5 animate-spin" />} Se connecter
            </button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            Pas encore membre ?{" "}
            <Link to="/adhesion-membre" data-testid="to-register" className="text-secondary font-medium hover:underline">
              Créer un compte
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
